import React, { useState, useEffect } from "react";
import Modal, { ModalFooter } from "@amzn/meridian/modal";
import Button from "@amzn/meridian/button";
import Select, { SelectOption } from "@amzn/meridian/select";
import Input from "@amzn/meridian/input";
import Box from "@amzn/meridian/box";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Alert from "@amzn/meridian/alert";
import Text from "@amzn/meridian/text";

import { FlattenedRule, IndirectStaffingChange, ModalFormData } from "../types";
import { validateModalForm, buildChangePayload } from "../utils";

interface UpdateIndirectHCModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (change: IndirectStaffingChange) => void;
  editData: FlattenedRule | null;
  isSaving: boolean;
  /** Server-side validation errors passed from the parent */
  serverValidationErrors?: string[];
}

const INITIAL_FORM_DATA: ModalFormData = {
  department: "",
  processPath: "",
  function: "",
  directRoleMapping: "",
  description: "",
  ruleAppliesOn: "",
  directBaseRatio: null,
  indirectRequirementRatio: null,
};

const UpdateIndirectHCModal: React.FC<UpdateIndirectHCModalProps> = ({
  open,
  onClose,
  onSave,
  editData,
  isSaving,
  serverValidationErrors = [],
}) => {
  const [ruleType, setRuleType] = useState<"FOR_EVERY" | "ALWAYS_HAVE">("ALWAYS_HAVE");
  const [formData, setFormData] = useState<ModalFormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditMode = editData !== null;

  // Reset form state when modal opens or editData changes
  useEffect(() => {
    if (open) {
      setErrors({});
      if (editData) {
        setRuleType(editData.ruleType);
        setFormData({
          department: editData.department,
          processPath: editData.processPath,
          function: editData.function,
          directRoleMapping: editData.directRoleMapping,
          description: editData.description,
          ruleAppliesOn: editData.ruleAppliesOn,
          directBaseRatio: editData.directBaseRatio,
          indirectRequirementRatio: editData.indirectRequirementRatio,
        });
      } else {
        setRuleType("ALWAYS_HAVE");
        setFormData(INITIAL_FORM_DATA);
      }
    }
  }, [open, editData]);

  const updateField = (field: keyof ModalFormData, value: string | number | null) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear field error on change
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSubmit = () => {
    const result = validateModalForm(formData, ruleType);
    if (!result.isValid) {
      setErrors(result.errors);
      return;
    }

    const action = isEditMode ? "UPDATE" : "CREATE";
    const change = buildChangePayload(action, formData, ruleType);
    onSave(change);
  };

  const handleNumberChange = (field: keyof ModalFormData, value: string) => {
    if (value === "") {
      updateField(field, null);
      return;
    }
    const parsed = Number(value);
    updateField(field, isNaN(parsed) ? null : parsed);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEditMode ? "Edit Indirect Staffing Rule" : "Add Indirect Staffing Rule"}
      scrollContainer="modal"
      closeLabel="Close"
    >
      <Box spacingInset="400">
        {serverValidationErrors.length > 0 && (
          <Box spacingInset="none none 400 none">
            <Alert type="error">
              {serverValidationErrors.map((err, idx) => (
                <Text key={idx}>{err}</Text>
              ))}
            </Alert>
          </Box>
        )}

        <Row widths="fill" spacing="400">
          <Column spacing="400">
            {/* Rule Type selector — shown in both Add and Edit modes */}
            <Box>
              <Text type="b200">Rule Type</Text>
              <Select
                value={ruleType}
                onChange={(value: string) =>
                  setRuleType(value as "FOR_EVERY" | "ALWAYS_HAVE")
                }
                width="100%"
              >
                <SelectOption value="ALWAYS_HAVE" label="Fixed HC (ALWAYS_HAVE)" />
                <SelectOption value="FOR_EVERY" label="Ratio Based (FOR_EVERY)" />
              </Select>
            </Box>

            {/* Department */}
            <Box>
              <Input
                value={formData.department}
                onChange={(value: string) => updateField("department", value)}
                label="Department"
                error={!!errors.department}
                errorMessage={errors.department}
              />
            </Box>

            {/* Process Path */}
            <Box>
              <Input
                value={formData.processPath}
                onChange={(value: string) => updateField("processPath", value)}
                label="Process Path"
                error={!!errors.processPath}
                errorMessage={errors.processPath}
              />
            </Box>

            {/* Function */}
            <Box>
              <Input
                value={formData.function}
                onChange={(value: string) => updateField("function", value)}
                label="Function"
                error={!!errors.function}
                errorMessage={errors.function}
              />
            </Box>

            {/* Direct Role Mapping */}
            <Box>
              <Input
                value={formData.directRoleMapping}
                onChange={(value: string) => updateField("directRoleMapping", value)}
                label="Direct Role Mapping"
                error={!!errors.directRoleMapping}
                errorMessage={errors.directRoleMapping}
              />
            </Box>

            {/* Description */}
            <Box>
              <Input
                value={formData.description}
                onChange={(value: string) => updateField("description", value)}
                label="Description"
              />
            </Box>

            {/* Indirect Requirement Ratio — always shown */}
            <Box>
              <Input
                value={
                  formData.indirectRequirementRatio !== null
                    ? String(formData.indirectRequirementRatio)
                    : ""
                }
                onChange={(value: string) =>
                  handleNumberChange("indirectRequirementRatio", value)
                }
                type="number"
                label={
                  ruleType === "ALWAYS_HAVE"
                    ? "Fixed Headcount"
                    : "Indirect Requirement Ratio"
                }
                error={!!errors.indirectRequirementRatio}
                errorMessage={errors.indirectRequirementRatio}
              />
            </Box>

            {/* Direct Base Ratio — only for FOR_EVERY */}
            {ruleType === "FOR_EVERY" && (
              <Box>
                <Input
                  value={
                    formData.directBaseRatio !== null
                      ? String(formData.directBaseRatio)
                      : ""
                  }
                  onChange={(value: string) =>
                    handleNumberChange("directBaseRatio", value)
                  }
                  type="number"
                  label="Direct Base Ratio"
                  error={!!errors.directBaseRatio}
                  errorMessage={errors.directBaseRatio}
                />
              </Box>
            )}

            {/* Rule Applies On — only for FOR_EVERY */}
            {ruleType === "FOR_EVERY" && (
              <Box>
                <Input
                  value={formData.ruleAppliesOn}
                  onChange={(value: string) => updateField("ruleAppliesOn", value)}
                  label="Rule Applies On"
                />
              </Box>
            )}
          </Column>
        </Row>
      </Box>

      <ModalFooter>
        <Row alignmentHorizontal="end" spacing="300">
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

export default UpdateIndirectHCModal;
